import React, { useState, useEffect } from 'react';
import { analyzeData } from './utils/analysis';
import { fetchAiModels, generateAiInsights } from './utils/ai';

// Components
import LandingPage from './components/landing/LandingPage';
import Dashboard from './components/dashboard/Dashboard';
import ProModal from './components/common/ProModal';
import DebugLog from './components/common/DebugLog';
import Footer from './components/common/Footer';

const SERPAnalyzer = () => {
  // --- States ---
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [jsonInput, setJsonInput] = useState('');
  const [showPasteArea, setShowPasteArea] = useState(false);
  const [showDataForSEOForm, setShowDataForSEOForm] = useState(false);
  const [loadingDataForSEO, setLoadingDataForSEO] = useState(false);
  const [dataForSEOParams, setDataForSEOParams] = useState({
    apiCredentials: '',
    keyword: '',
    languageCode: 'it',
    device: 'mobile',
    os: 'android',
    depth: 10,
    paaDepth: 2,
    useCorsProxy: false
  });
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebugLogs, setShowDebugLogs] = useState(false);
  const [clientDomain, setClientDomain] = useState('');
  const [showClientConfig, setShowClientConfig] = useState(false);
  const [proCode, setProCode] = useState('');
  const [isPro, setIsPro] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    provider: 'anthropic',
    model: 'claude-3-sonnet-20240229',
    apiKeys: {
      google: '',
      openai: '',
      anthropic: ''
    }
  });
  const [availableModels, setAvailableModels] = useState({
    anthropic: [],
    openai: [],
    google: []
  });
  const [loadingModels, setLoadingModels] = useState(false);

  // --- Effects ---
  useEffect(() => {
    const savedProStatus = localStorage.getItem('serpAnalyzerPro');
    if (savedProStatus === 'SIMBUS82') {
      setIsPro(true);
    }

    const savedAiConfig = localStorage.getItem('serpAnalyzerAiConfig');
    if (savedAiConfig) {
      try {
        setAiConfig(JSON.parse(savedAiConfig));
      } catch (e) {
        console.error('Errore nel caricamento della configurazione AI:', e);
      }
    }

    const savedClientDomain = localStorage.getItem('serpAnalyzerClientDomain');
    if (savedClientDomain) {
      setClientDomain(savedClientDomain);
    }
  }, []);

  useEffect(() => {
    if (isPro) {
      localStorage.setItem('serpAnalyzerAiConfig', JSON.stringify(aiConfig));
    }
  }, [aiConfig, isPro]);

  useEffect(() => {
    if (isPro && clientDomain) {
      localStorage.setItem('serpAnalyzerClientDomain', clientDomain);
    }
  }, [clientDomain, isPro]);

  // --- Helpers ---
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev, { timestamp, message, type }]);
  };

  // --- Handlers ---
  const handleProCodeSubmit = () => {
    if (proCode.trim() === 'SIMBUS82') {
      setIsPro(true);
      localStorage.setItem('serpAnalyzerPro', 'SIMBUS82');
      setShowProModal(false);
      setProCode('');
      addLog('✓ Modalità PRO attivata', 'success');
    } else {
      addLog('❌ Codice PRO non valido', 'error');
      setError('Codice PRO non valido');
    }
  };

  const handleDataForSEOSearch = async () => {
    setLoadingDataForSEO(true);
    setError(null);
    addLog('=== Inizio ricerca DataForSEO ===', 'info');

    try {
      addLog('Validazione parametri...', 'info');
      if (!dataForSEOParams.apiCredentials) throw new Error('Le credenziali API sono obbligatorie');
      if (!dataForSEOParams.keyword) throw new Error('La keyword è obbligatoria');
      addLog('✓ Parametri validi', 'success');

      const credentials = dataForSEOParams.apiCredentials.trim();
      const apiUrl = 'https://api.dataforseo.com/v3/serp/google/organic/live/advanced';
      const fetchUrl = dataForSEOParams.useCorsProxy ? `https://cors-anywhere.herokuapp.com/${apiUrl}` : apiUrl;

      const payload = [{
        keyword: dataForSEOParams.keyword,
        location_code: 2380,
        language_code: dataForSEOParams.languageCode,
        device: dataForSEOParams.device,
        os: dataForSEOParams.os,
        depth: dataForSEOParams.depth,
        calculate_rectangles: true,
        group_organic_results: true,
        load_async_ai_overview: true,
        people_also_ask_click_depth: dataForSEOParams.paaDepth
      }];

      addLog('Invio richiesta HTTP...', 'info');
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Errore API: ${response.status} - ${errorText}`);
      }

      const jsonData = await response.json();
      if (!jsonData.tasks || !jsonData.tasks[0] || !jsonData.tasks[0].result) throw new Error('Risposta API non valida');

      addLog('✓ Risposta valida ricevuta', 'success');

      const transformedData = { result: jsonData.tasks[0].result };
      setData(transformedData);
      setShowDataForSEOForm(false);
      setInsights(null);
      addLog('=== Ricerca completata ===', 'success');

    } catch (err) {
      addLog(`❌ ERRORE: ${err.message}`, 'error');
      setError(`Errore nella ricerca: ${err.message}`);
    } finally {
      setLoadingDataForSEO(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      if (!jsonData.result || !jsonData.result[0] || !jsonData.result[0].items) {
        throw new Error('Formato JSON non valido. Assicurati che il file contenga result[0].items');
      }

      setData(jsonData);
      setError(null);
      setShowPasteArea(false);
      setShowDataForSEOForm(false);
      setInsights(null);

      const target = jsonData.tasks?.[0]?.data?.target || jsonData.data?.target;
      if (target) {
        setClientDomain(target);
        addLog(`✓ Dominio cliente rilevato: ${target}`, 'success');
      }

      addLog('✓ File JSON caricato con successo', 'success');
    } catch (err) {
      setError(`Errore nel caricamento: ${err.message}`);
      setData(null);
    }
  };

  const handleJsonPaste = () => {
    try {
      const jsonData = JSON.parse(jsonInput);
      if (!jsonData.result || !jsonData.result[0] || !jsonData.result[0].items) {
        throw new Error('Formato JSON non valido. Assicurati che il JSON contenga result[0].items');
      }

      setData(jsonData);
      setError(null);
      setJsonInput('');
      setShowPasteArea(false);
      setInsights(null);

      const target = jsonData.tasks?.[0]?.data?.target || jsonData.data?.target;
      if (target) setClientDomain(target);
      addLog('✓ JSON incollato caricato con successo', 'success');
    } catch (err) {
      setError(`Errore nel parsing: ${err.message}`);
    }
  };

  const generateInsights = async (analysisData) => {
    setLoadingInsights(true);
    addLog(`=== Generazione Insights AI (${aiConfig.provider}) ===`, 'info');

    try {
      const parsedInsights = await generateAiInsights(analysisData, aiConfig, clientDomain);
      setInsights(parsedInsights);
      addLog('✓ Insights generati con successo', 'success');
    } catch (err) {
      addLog(`❌ ERRORE AI: ${err.message}`, 'error');
      setInsights({
        serpFeatures: "Errore nella generazione",
        competitiveness: err.message,
        searchIntent: "Errore",
        contentStrategy: "Controlla la configurazione API nelle impostazioni PRO",
        actionItems: ["Errore nel caricamento"],
        quickWins: ["Errore nel caricamento"]
      });
    } finally {
      setLoadingInsights(false);
    }
  };

  const fetchModels = async (provider) => {
    const apiKey = aiConfig.apiKeys[provider];
    if (!apiKey) {
      addLog(`❌ Impossibile caricare i modelli: Chiave API ${provider} mancante`, 'error');
      return;
    }

    setLoadingModels(true);
    addLog(`Caricamento modelli da ${provider}...`, 'info');

    try {
      const models = await fetchAiModels(provider, apiKey);
      setAvailableModels(prev => ({ ...prev, [provider]: models }));
      addLog(`✓ ${models.length} modelli caricati per ${provider}`, 'success');
    } catch (err) {
      addLog(`❌ Errore caricamento modelli: ${err.message}`, 'error');
    } finally {
      setLoadingModels(false);
    }
  };

  // --- Render Analysis ---
  const analysis = data ? analyzeData(data) : null;

  return (
    <div className="w-full bg-gray-50 min-h-screen font-sans">
      <main className="py-8">
        {!data ? (
          <div className="w-full max-w-4xl mx-auto p-6">
            <LandingPage
              isPro={isPro}
              setShowProModal={setShowProModal}
              handleFileUpload={handleFileUpload}
              showPasteArea={showPasteArea}
              setShowPasteArea={setShowPasteArea}
              jsonInput={jsonInput}
              setJsonInput={setJsonInput}
              handleJsonPaste={handleJsonPaste}
              showDataForSEOForm={showDataForSEOForm}
              setShowDataForSEOForm={setShowDataForSEOForm}
              dataForSEOParams={dataForSEOParams}
              setDataForSEOParams={setDataForSEOParams}
              handleDataForSEOSearch={handleDataForSEOSearch}
              loadingDataForSEO={loadingDataForSEO}
            />
          </div>
        ) : (
          <Dashboard
            analysis={analysis}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            insights={insights}
            loadingInsights={loadingInsights}
            generateInsights={generateInsights}
            clientDomain={clientDomain}
            setClientDomain={setClientDomain}
            showClientConfig={showClientConfig}
            setShowClientConfig={setShowClientConfig}
            isPro={isPro}
            aiConfig={aiConfig}
            setAiConfig={setAiConfig}
            availableModels={availableModels}
            fetchModels={fetchModels}
            loadingModels={loadingModels}
            onNewSearch={() => {
              setData(null);
              setInsights(null);
              addLog('Inizio nuova ricerca, configurazione preservata', 'info');
            }}
          />
        )}

        <DebugLog
          debugLogs={debugLogs}
          setDebugLogs={setDebugLogs}
          showDebugLogs={showDebugLogs}
          setShowDebugLogs={setShowDebugLogs}
        />
      </main>

      <Footer isPro={isPro} />

      <ProModal
        showProModal={showProModal}
        setShowProModal={setShowProModal}
        proCode={proCode}
        setProCode={setProCode}
        error={error}
        setError={setError}
        handleProCodeSubmit={handleProCodeSubmit}
      />
    </div>
  );
};

export default SERPAnalyzer;
